import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";

import { Button, FactorsBanner, Loading, StyledGrid } from "components/simple";
import { GetFactors } from "graphql/bhi/factors.gql";
import BrainHealthIndex from "./BrainHealthIndex";
import Header from "./Header";

import UserAssessmentsSection from "components/pages/UserAssessments/UserAssessmentsSection";
import { spacingDefaults } from "style/constants";
import { NewCycle } from "models/userAssessmentCycle";
import { GetFactorsData } from "models/bhi";
import { PillarName } from "models/score";

const getIndexDelta = (oldIndex: number | undefined, newIndex: number) => {
  return oldIndex ? parseFloat((newIndex - oldIndex).toFixed(1)) : undefined;
};

export const getNewPillarScore = (cycle: NewCycle, pillarName: string): number => {
  const pillarScoreValue =
    cycle.bhiRefactorPillarScores.find(pillar => pillar.pillarName === pillarName)?.value || 0;

  return Math.round(pillarScoreValue);
};

interface CycleSummaryProps {
  participantAssessmentCycles: NewCycle[];
  selectedCycleIndex: number;
  setSelectedCycleIndex: React.Dispatch<React.SetStateAction<number>>;
}

const CycleSummary: React.FC<CycleSummaryProps> = ({
  participantAssessmentCycles,
  selectedCycleIndex,
  setSelectedCycleIndex
}) => {
  const [selectedFactor, setSelectedFactor] = useState<PillarName | undefined>();

  const { data, loading: factorInfoLoading } = useQuery<GetFactorsData>(GetFactors);

  const getOldIndex = () => {
    return selectedCycleIndex != 0
      ? getNewPillarScore(participantAssessmentCycles[selectedCycleIndex - 1], PillarName.NEW_INDEX)
      : undefined;
  };

  if (factorInfoLoading) {
    return <Loading />;
  }

  if (!data || participantAssessmentCycles.length == 0) {
    return <></>;
  }

  const selectedCycle = participantAssessmentCycles[selectedCycleIndex];
  const clarity = getNewPillarScore(selectedCycle, PillarName.CLARITY);
  const connectedness = getNewPillarScore(selectedCycle, PillarName.CONNECTEDNESS);
  const emotionalBalance = getNewPillarScore(selectedCycle, PillarName.EMOTIONAL_BALANCE);
  const index = getNewPillarScore(selectedCycle, PillarName.NEW_INDEX);
  const oldIndex = getOldIndex();
  const indexDelta = getIndexDelta(oldIndex, index);
  const userAssessmentCycleId = participantAssessmentCycles[selectedCycleIndex].id;

  return (
    <>
      {selectedCycle && (
        <StyledGrid container direction="column" position="relative">
          <Header
            selectedCycleIndex={selectedCycleIndex}
            selectedCycleDate={selectedCycle.cycleCompletionDate}
            setSelectedCycleIndex={setSelectedCycleIndex}
            isPreviousDisabled={selectedCycleIndex === 0}
            isNextDisabled={selectedCycleIndex === participantAssessmentCycles.length - 1}
          />
          <StyledGrid item marginTop="5.5rem" xs={12}>
            <FactorsBanner
              clarity={clarity}
              connectedness={connectedness}
              emotionalBalance={emotionalBalance}
              index={index}
              indexDelta={indexDelta}
              setSelectedFactor={setSelectedFactor}
              selectedFactor={selectedFactor}
            />
          </StyledGrid>
          <StyledGrid item container xs={12}>
            <BrainHealthIndex
              currentCycleIndex={selectedCycleIndex}
              cycles={participantAssessmentCycles}
              selectedFactor={selectedFactor}
              factorInfo={data.factors}
            />
          </StyledGrid>
          <StyledGrid alignSelf="center" item marginTop={spacingDefaults.large} xs={12}>
            <Link to={`/user-assessments/${userAssessmentCycleId}?refactor`}>
              <Button squared>View Assessments</Button>
            </Link>
          </StyledGrid>
          <StyledGrid item xs={12} padding={`0 ${spacingDefaults.xsmall}`}>
            <UserAssessmentsSection userAssessmentCycleId={userAssessmentCycleId} />
          </StyledGrid>
        </StyledGrid>
      )}
    </>
  );
};

export default CycleSummary;
